import type { AuthTranslations } from "../../types/auth";

export const auth: AuthTranslations = {
  emailVerificationRequired: "Your email address needs to be verified before you can log in.",
  verificationEmailSent: "Verification email sent",
  verificationEmailSentDesc: "Please check your inbox and click the verification link.",
  loadingInvitation: "Loading invitation details...",
  signingUpToJoin: "You're signing up to join",
  asRole: "as",
  emailFromInvitation: "Email is set from your invitation",
  signupFailed: "Sign Up Failed",
  accountCreatedSuccessfully: "Your account has been created successfully.",
  goToLogin: "Go to Login",
  signUp: "Sign Up",
  noInvitationToken: "No invitation token provided",
  invalidOrExpiredInvitation: "Invalid or expired invitation link",
  invitationExpired: "This invitation has expired. Please ask for a new invitation.",
  invitationAlreadyAccepted: "This invitation has already been accepted.",
  invitationNoLongerValid: "This invitation is no longer valid.",
  failedToLoadInvitation: "Failed to load invitation details. Please try again.",
  invitationError: "Invitation Error",
  unableToProcessInvitation: "Unable to process invitation",
  invitationErrorDescription: "This invitation link may be incomplete, expired, or already used.",
  invitationRetryDescription:
    "Something interrupted the invitation process. Your invitation may still be valid.",
  invitationAccountMismatchDescription:
    "This invitation belongs to a different email address than the account currently signed in.",
  invitationErrorGuidanceTitle: "Need a new invitation?",
  invitationErrorGuidance:
    "Ask the person who invited you to send a new link. If that does not solve it, Franklin support can help.",
  invitationRetryGuidanceTitle: "Try the invitation again",
  invitationRetryGuidance:
    "Retry the invitation now. If the problem continues, contact Franklin support for help.",
  invitationAccountMismatchGuidanceTitle: "Use the invited account",
  invitationAccountMismatchGuidance:
    "Go to login and use the email address shown above, or ask the sender to invite this account instead.",
  contactSupport: "Contact support",
  goToDashboard: "Go to dashboard",
  welcomeAboard: "Welcome Aboard!",
  successfullyJoinedOrg:
    "You've successfully joined {organizationName}. Redirecting you to the dashboard...",
  youreInvited: "You're Invited!",
  createAccountToJoin: "Create an account to join {organizationName}",
  signUpWithEmailAddress: "Please sign up using the email address:",
  createAccount: "Create Account",
  alreadyHaveAccountLogin: "Already have an account? Log In",
  invitationHeading: "You're invited to {organizationName}",
  invitationSubtitle: "Join your team on Franklin.",
  invitationEmailLabel: "Email",
  invitationEmailHint: "Sign up with this address",
  invitationRoleLabel: "Role",
  invitationAlreadyHasAccount: "Already have an account?",
  invitationLogin: "Log In",
  invitationExpiresInOneDay: "This invitation expires in 1 day, on {date}",
  invitationExpiresInDays: "This invitation expires in {count} days, on {date}",
  invitationExpiresUnderOneDay: "This invitation expires in less than 24 hours, on {date}",
  invitationExpiredOn: "This invitation expired on {date}",
  invitationExpiryUnavailable: "The invitation expiry date is unavailable",
  acceptInvitation: "Accept Invitation",
  invitedToJoinOrg: "You've been invited to join an organisation",
  accepting: "Accepting...",
  failedToSignOut: "Failed to sign out",
  joinedOrgAsRole: "You've joined {organizationName} as {role}",
  welcomeLetsGetStarted: "Welcome! Let's Get Started",
  createOrgToManageBuildings: "Create your organisation to start managing buildings",
  joinExistingOrganization: "Join an existing organisation",
  loadingInvitations: "Loading invitations...",
  pendingInvitations: "You have {count} pending invitation(s)",
  noPendingInvitations: "No pending invitations",
  noPendingInvites:
    "No pending invitations. If there should be ask your organisation admin for an invite.",
  accept: "Accept",
  signOut: "Sign Out",
  organizationSelected: "Organisation selected",
  switchedToOrganization: "You've switched to {organizationName}.",
  userNotFound: "User not found. Please try logging in again.",
  organizationCreated: 'Organisation "{orgName}" created successfully.',
  createNewOrganization: "Create New Organisation",
  createOrgDescription: "Enter a name for your new organisation. This name can be changed later.",
  checkYourEmail: "Check Your Email",
  checkEmailToActivate: "Please check your email to activate your account",
  sentVerificationLinkTo: "We've sent a verification link to",
  clickLinkToActivate:
    "Click the link in your email to activate your account and start using the platform.",
  resendIn: "Resend in {countdown}s",
  resendVerificationEmail: "Resend verification email",
  backToLogin: "Back to Login",
  verificationEmailSentToast: "Verification email sent! Please check your inbox.",
  failedToResendEmail: "Failed to resend verification email",
  authenticationError: "Authentication Error",
  permissionLoadFailed: "Access could not be checked. Please try again.",
  checkingAccess: "Checking access",
  authSystemError:
    "There was an issue with the authentication system. Please try logging in again.",
  adminPrivilegesRequired: "Access denied: Admin privileges required",
  authenticationRequired: "Authentication required",
  insufficientPrivileges: "Access denied: Insufficient privileges",
  featureComingSoon: "{featureName} is currently in development and will be available soon.",
  stayTuned: "We're working hard to bring you this feature. Stay tuned for updates!",
  emailMismatchError:
    "This invitation was sent to {inviteEmail}. You are currently logged in as {currentEmail}. Please log out and sign in with the correct account, or sign up with the invited email.",
  expires: "Expires {date}",
  welcome: "Welcome!",
  accountCreatedDesc: "Your account has been created successfully.",
  failedToCreateProfile: "Failed to create user profile. Please try again.",
  failedToAcceptInvitation: "Failed to accept invitation",
  failedToCreateOrganization: "Failed to create organisation",
  failedToAssignOwnerRole: "Failed to assign owner role",
  tosTitle: "Terms of Service and Privacy Policy",
  tosBody: "Please review and accept our Terms of Service and Privacy Policy to continue.",
  tosAccept: "Accept",
  tosCheckbox: "I have read and accept the {terms} and {privacy}",
  tosTermsLink: "Terms of Service",
  tosPrivacyLink: "Privacy Policy",
  tosAccepting: "Accepting...",
  tosFailedToAccept: "Failed to accept Terms of Service. Please try again.",
  tosSignOut: "Sign out",
  tosProfileUnavailableTitle: "We could not load your account",
  tosProfileUnavailableBody:
    "Your profile did not load, so we cannot confirm that you have accepted the Terms of Service. Try again, or sign out and sign back in.",
  tosProfileMissingTitle: "Your account is not fully set up",
  tosProfileMissingBody:
    "We signed you in, but your account has no profile, so we cannot confirm that you have accepted the Terms of Service. Please contact support.",
  continueWithGoogle: "Continue with Google",
  orContinueWith: "or",
  signingIn: "Signing you in...",
  signingInDesc: "Finishing sign-in with Google.",
  googleSignInFailed: "Sign-in failed",
  googleSignInFailedDesc: "We could not complete the sign-in with Google. Please try again.",
};
